import { useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useSubscription } from "../hooks/useSubscription";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { VariationsStats } from "../hooks/useVariationsStats";
import { useAiModel } from "../hooks/useAiModel";
import { logError } from "../lib/logError";

interface DashboardInsightProps {
  stats: VariationsStats;
  projectCount: number;
}

export function DashboardInsight({
  stats,
  projectCount,
}: DashboardInsightProps) {
  const [insight, setInsight] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aiModel = useAiModel();
  const { logAiUsage, canUseAi } = useSubscription();

  const generateInsight = async () => {
    const check = canUseAi("dashboard_insights");
    if (!check.allowed) {
      setError(check.reason || "AI usage not available");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Prepare payload from props (Client-side calculated stats)
      const payload = {
        totalPendingVars: stats.totalPending,
        // Stats root doesn't have pendingCount, but we can derive it or pass 0 for now as it's less critical
        // OR we can sum it from projectStats
        pendingVarsCount: stats.projectStats.reduce(
          (sum, p) => sum + p.pendingCount,
          0,
        ),
        overLimitProjects: stats.projectStats
          .filter(
            (p) => p.claimsToDate > p.contractValue + p.approvedVariations,
          )
          .map((p) => ({
            name: p.projectName,
            claimed: p.claimsToDate,
            contract: p.contractValue + p.approvedVariations,
            over: p.claimsToDate - (p.contractValue + p.approvedVariations),
          })),
        nearLimitProjects: stats.projectStats
          .filter((p) => {
            const budget = p.contractValue + p.approvedVariations;
            return p.claimsToDate > budget * 0.9 && p.claimsToDate <= budget;
          })
          .map((p) => ({
            name: p.projectName,
            claimed: p.claimsToDate,
            contract: p.contractValue + p.approvedVariations,
          })),
        totalProjects: projectCount,
        criticalCount: stats.projectStats.filter(
          (p) => p.claimsToDate > p.contractValue + p.approvedVariations,
        ).length,
        topPendingProjects: stats.projectStats
          .filter((p) => p.pendingVariations > 0)
          .sort((a, b) => b.pendingVariations - a.pendingVariations)
          .slice(0, 10)
          .map((p) => ({
            name: p.projectName,
            pendingAmount: p.pendingVariations,
            pendingCount: p.pendingCount,
          })),
      };

      const { data, error } = await supabase.functions.invoke(
        "generate-dashboard-insights",
        {
          body: { ...payload, ai_model: aiModel },
        },
      );

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setInsight(data.analysis);

      // Log AI credit usage with token data
      if (data.usage) {
        logAiUsage("dashboard_insights", data.usage);
      } else {
        logAiUsage("dashboard_insights");
      }
    } catch (err) {
      logError("Failed to generate insight:", err);
      // More descriptive error for CORS/Network issues
      if (err instanceof TypeError && err.message === "Failed to fetch") {
        setError("Connection error. Please check your network or try again.");
      } else {
        setError(
          err instanceof Error ? err.message : "Unable to generate insight.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!insight && !loading && !error) {
    return (
      <div
        className="card"
        style={{
          background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
          border: "1px solid #bbf7d0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#16a34a",
              }}
            >
              <Sparkles size={20} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#166534",
                  marginBottom: 2,
                }}
              >
                AI Commercial Analyst
              </h3>
              <p style={{ fontSize: "0.8rem", color: "#15803d" }}>
                Generate an executive summary of risks & bottlenecks.
              </p>
            </div>
          </div>
          <button
            onClick={generateInsight}
            className="btn"
            style={{
              background: "#16a34a",
              color: "white",
              border: "none",
              boxShadow: "0 2px 4px rgba(22, 163, 74, 0.2)",
            }}
          >
            Generate Insight
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ borderTop: "4px solid #8b5cf6" }}>
      <div
        className="card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={18} style={{ color: "#8b5cf6" }} />
          <h3 className="card-title">AI Executive Summary</h3>
        </div>
        <button
          onClick={generateInsight}
          disabled={loading}
          className="btn btn-ghost btn-sm"
          style={{ color: "#6b7280" }}
        >
          {loading ? (
            <RefreshCw size={16} className="spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          {loading ? "Analyzing..." : "Refresh"}
        </button>
      </div>

      <div style={{ lineHeight: 1.6, fontSize: "0.9rem", color: "#374151" }}>
        {error ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              color: "#dc2626",
              alignItems: "center",
              padding: "12px",
              background: "#fef2f2",
              borderRadius: 8,
            }}
          >
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : loading ? (
          <div
            style={{ padding: "20px", textAlign: "center", color: "#6b7280" }}
          >
            <div className="spinner" style={{ margin: "0 auto 12px" }} />
            Analyzing project financials...
          </div>
        ) : (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                 
                table: ({ node, ...props }) => (
                  <div style={{ overflowX: "auto", margin: "1rem 0" }}>
                    <table
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        fontSize: "0.85rem",
                      }}
                      {...props}
                    />
                  </div>
                ),
                 
                th: ({ node, ...props }) => (
                  <th
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "8px",
                      background: "#f9fafb",
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                    {...props}
                  />
                ),
                 
                td: ({ node, ...props }) => (
                  <td
                    style={{ border: "1px solid #e5e7eb", padding: "8px" }}
                    {...props}
                  />
                ),
              }}
            >
              {insight || ""}
            </ReactMarkdown>
            <div
              style={{
                marginTop: 16,
                borderTop: "1px solid #e5e7eb",
                paddingTop: 8,
                fontSize: "0.7rem",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              AI-generated content. Verify all figures with project data.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
